import React, { PropsWithChildren, ReactElement } from 'react';
import { Col, Label, Row } from 'reactstrap';
import classnames from 'classnames';
import './FormRow.scss';

/** A FormRow component for attaching labels to inputs and building forms */

export interface FormRowProps {
  align?: 'top' | 'center';
  borderless?: boolean;
  className?: string;
  helpText?: string;
  label: ReactElement | string;
  name: string;
  optional?: boolean;
  stacked?: boolean;
  toggleContainer?: boolean;
  toggleLeft?: boolean;
  subLabel?: string;
  labelColumnRatio?: number;
  labelTextAlign?: 'left' | 'right';
  fullWidth?: boolean;
  labelMinWidth?: string;
}

const FormRow: React.FC<PropsWithChildren<FormRowProps>> = ({
  align = 'top',
  borderless = false,
  children,
  className,
  helpText,
  label,
  name,
  optional = false,
  stacked = false,
  toggleContainer = false,
  toggleLeft = false,
  subLabel,
  labelColumnRatio,
  labelTextAlign,
  fullWidth,
  labelMinWidth,
}) => {
  const columnRatio = labelColumnRatio
    ? [labelColumnRatio, 12 - labelColumnRatio]
    : [3, 9];
  const inlineLabelWidth = toggleLeft ? '' : columnRatio[0];
  let labelMd = inlineLabelWidth;
  if (fullWidth) {
    labelMd = 'auto';
  } else if (stacked) {
    labelMd = 12;
  }

  const trimmedSubLabel = subLabel?.trim();
  const hasWrappedSubLabel =
    trimmedSubLabel?.startsWith('(') && trimmedSubLabel?.endsWith(')');
  let normalizedSubLabel: string | undefined;
  if (trimmedSubLabel) {
    normalizedSubLabel = hasWrappedSubLabel
      ? trimmedSubLabel.slice(1, -1).trim()
      : trimmedSubLabel;
  }
  const hasNormalizedSubLabel = Boolean(normalizedSubLabel);
  let optionalHint: string | undefined;
  if (optional) {
    optionalHint = hasNormalizedSubLabel
      ? `(Optional, ${normalizedSubLabel})`
      : '(Optional)';
  }
  let subLabelHint: string | undefined;
  if (!optional && hasNormalizedSubLabel) {
    subLabelHint = hasWrappedSubLabel
      ? trimmedSubLabel
      : `(${normalizedSubLabel})`;
  }

  return (
    <div
      className={classnames(
        className,
        'form__form-group',
        {
          borderless,
          optional,
          'toggle-container': toggleContainer,
          'toggle-left': toggleLeft,
        },
        stacked ? 'stacked' : 'horizontal'
      )}
    >
      <Row
        className={classnames('form-row', {
          'align-items-top': align === 'top' && !toggleContainer,
          'align-items-center': align === 'center' || toggleContainer,
          'no-gutters': toggleLeft,
        })}
        style={labelMinWidth ? { flexWrap: 'nowrap' } : undefined}
      >
        {label && (
          <Label
            className={classnames(
              {
                'order-2 ml-3': toggleLeft,
                'text-right': labelTextAlign === 'right',
                'text-left': labelTextAlign === 'left',
              },
              'form__form-group-label'
            )}
            htmlFor={name}
            md={labelMd}
            xs={toggleContainer ? 'auto' : undefined}
            style={labelMinWidth ? { minWidth: labelMinWidth } : undefined}
          >
            {label}
            {optionalHint && (
              <small
                className={classnames('help-text optional-flag', {
                  'ml-1': stacked,
                  'd-block': !stacked,
                })}
              >
                {optionalHint}
              </small>
            )}
            {!optional && subLabelHint && (
              <small
                className={classnames('help-text optional-flag', {
                  'ml-1': stacked,
                  'd-block': !stacked,
                })}
              >
                {subLabelHint}
              </small>
            )}
          </Label>
        )}
        <Col
          xs={fullWidth ? undefined : (toggleContainer && 'auto') || 12}
          md={
            fullWidth
              ? undefined
              : !toggleLeft && (!label || stacked ? 12 : columnRatio[1])
          }
        >
          <div className="input-container">
            <div className="form__form-group-input-wrap--error-below">
              {children}
              {helpText && (
                <small className="help-text d-block mt-1">{helpText}</small>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default FormRow;
